import { useConfirm } from "@/components/DialogProvider";
import { MentionInput } from "@/components/mentions/MentionInput";
import { MentionRenderer } from "@/components/mentions/MentionRenderer";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import { useProjectNotes } from "@/features/projects/hooks/useProjectNotes";
import { parseMentions } from "@/lib/mentions/types";
import { formatDateTimeShort } from "@/lib/utils";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/dashboard/admin/projects/$projectId/notes/")({
  component: RouteComponent,
});

function RouteComponent() {
  const { projectId } = Route.useParams();
  const { userProfile } = useAuth();
  const { notes, loading, adding, updateNote, deleteNote, addNote, markAsRead } = useProjectNotes(
    projectId,
    userProfile?.uid,
  );
  const confirm = useConfirm();
  const [newNote, setNewNote] = useState("");
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingNoteContent, setEditingNoteContent] = useState("");

  // Mark unread notes as read when component is viewed
  useEffect(() => {
    if (!userProfile?.uid || loading) return;

    const unreadNotes = notes.filter((note) => !note.readBy?.includes(userProfile.uid));
    unreadNotes.forEach((note) => {
      markAsRead(note.id);
    });
  }, [notes, userProfile?.uid, loading, markAsRead]);

  const handleSendNote = async () => {
    if (!newNote.trim() || !userProfile) return;

    try {
      const mentions = parseMentions(newNote.trim());
      await addNote({
        content: newNote.trim(),
        authorId: userProfile.uid,
        authorName: userProfile.name,
        authorEmail: userProfile.email,
        mentions: mentions.length > 0 ? mentions : undefined,
      });
      setNewNote("");
    } catch (error) {
      console.error("Failed to add note:", error);
      toast.error("Failed to add note. Please try again.");
    }
  };

  const handleEditNote = (noteId: string) => {
    const note = notes.find((n) => n.id === noteId);
    if (note) {
      setEditingNoteId(noteId);
      setEditingNoteContent(note.content);
    }
  };

  const handleSaveEdit = async () => {
    if (!editingNoteId || !editingNoteContent.trim()) return;

    try {
      const mentions = parseMentions(editingNoteContent.trim());
      await updateNote(editingNoteId, {
        content: editingNoteContent.trim(),
        mentions: mentions.length > 0 ? mentions : undefined,
      });
      setEditingNoteId(null);
      setEditingNoteContent("");
    } catch (error) {
      console.error("Failed to update note:", error);
      toast.error("Failed to update note. Please try again.");
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    const confirmed = await confirm({
      title: "Delete Note",
      description: "Are you sure you want to delete this note? This action cannot be undone.",
      confirmText: "Delete",
      cancelText: "Cancel",
    });

    if (confirmed) {
      try {
        await deleteNote(noteId);
      } catch (error) {
        console.error("Failed to delete note:", error);
        toast.error("Failed to delete note. Please try again.");
      }
    }
  };

  const formatTimestamp = (timestamp: any) => {
    try {
      return formatDateTimeShort(timestamp);
    } catch {
      return "Unknown time";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {/* Tab Content */}
      <div className="mt-4">
        <div className="rounded border border-gray-200 bg-white">
          {/* Header */}
          <div className="border-b border-gray-200 px-4 py-3">
            <h3 className="text-base font-semibold text-gray-900">Project notes</h3>
          </div>

          {/* Notes Content */}
          {notes.length === 0 && !editingNoteId ? (
            /* Empty State */
            (<div className="flex flex-col items-center justify-center py-16">
              <svg
                width="160"
                height="84"
                viewBox="0 0 160 84"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="mb-6"
              >
                <path
                  d="M64.5723 15.7856C61.7319 15.7856 59.4294 18.0882 59.4294 20.9285C59.4294 23.7688 61.7319 26.0714 64.5723 26.0714H34.858C32.0177 26.0714 29.7151 28.3739 29.7151 31.2142C29.7151 34.0545 32.0177 36.3571 34.858 36.3571H53.1437C55.984 36.3571 58.2865 38.6596 58.2865 41.4999C58.2865 44.3403 55.984 46.6428 53.1437 46.6428H25.7151C22.8748 46.6428 20.5723 48.9453 20.5723 51.7856C20.5723 54.626 22.8748 56.9285 25.7151 56.9285H53.1437C50.3034 56.9285 48.0008 59.231 48.0008 62.0714C48.0008 64.9117 50.3034 67.2142 53.1437 67.2142H117.144C119.984 67.2142 122.287 64.9117 122.287 62.0714C122.287 59.231 119.984 56.9285 117.144 56.9285H115.715C112.875 56.9285 110.572 54.626 110.572 51.7856C110.572 48.9453 112.875 46.6428 115.715 46.6428H122.858C125.698 46.6428 128.001 44.3403 128.001 41.4999C128.001 38.6596 125.698 36.3571 122.858 36.3571H106.858C109.698 36.3571 112.001 34.0545 112.001 31.2142C112.001 28.3739 109.698 26.0714 106.858 26.0714H135.429C138.27 26.0714 140.572 23.7688 140.572 20.9285C140.572 18.0882 138.27 15.7856 135.429 15.7856H64.5723Z"
                  fill="#EDE9FE"
                />
                <path
                  d="M135.429 46.6428C138.27 46.6428 140.572 44.3403 140.572 41.4999C140.572 38.6596 138.27 36.3571 135.429 36.3571C132.589 36.3571 130.287 38.6596 130.287 41.4999C130.287 44.3403 132.589 46.6428 135.429 46.6428Z"
                  fill="#EDE9FE"
                />
                <path
                  d="M52.5713 1.67871H95.4521C96.3642 1.6788 97.246 2.00515 97.9385 2.59863L111.059 13.8438C111.905 14.5697 112.392 15.6297 112.393 16.7451V78.6426C112.393 80.7531 110.682 82.4648 108.571 82.4648H52.5713C50.4608 82.4648 48.75 80.7531 48.75 78.6426V5.5C48.7501 3.38959 50.4609 1.67879 52.5713 1.67871Z"
                  fill="white"
                  stroke="#7F22FE"
                  strokeWidth="1.5"
                />
                <path
                  d="M73.1535 6.64307H54.858C53.5957 6.64307 52.5723 7.6665 52.5723 8.92878V76.3574C52.5723 77.6196 53.5957 78.6431 54.858 78.6431H106.287C107.549 78.6431 108.572 77.6196 108.572 76.3574V66.0716C86.6137 54.7966 72.7378 31.2739 73.1535 6.64307Z"
                  fill="#EDE9FE"
                />
                <path
                  d="M97.7143 2.07153V13.5001C97.7143 15.0781 98.9935 16.3572 100.571 16.3572H107.714C108.188 16.3572 108.571 16.741 108.571 17.2144C108.571 17.6878 108.188 18.0715 107.714 18.0715H100.571C98.0467 18.0715 96 16.0248 96 13.5001V2.07153H97.7143Z"
                  fill="#7F22FE"
                />
                <path
                  d="M58.2852 67.2144H78.8566M58.2852 16.9287H83.428H58.2852ZM58.2852 28.3573H100.571H58.2852ZM58.2852 42.0716H100.571H58.2852ZM58.2852 54.643H83.428H58.2852Z"
                  stroke="#8E51FF"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M128.893 29.9414L130.248 31.2959C131.783 32.8312 131.783 35.3203 130.248 36.8555L100.329 66.7737L89.9582 70.2308L93.4153 59.8596L123.333 29.9414C124.869 28.4062 127.358 28.4062 128.893 29.9414Z"
                  fill="white"
                  stroke="#7F22FE"
                  strokeWidth="1.5"
                  strokeLinejoin="round"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M129.163 32.0131C130.314 32.9249 130.507 34.5967 129.595 35.7472C129.531 35.8288 129.461 35.9066 129.388 35.98L125.223 40.1256L123.022 38.2129L129.163 32.0131Z"
                  fill="#EDE9FE"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M126.98 40.126C125.601 39.3619 124.42 38.4883 123.436 37.5052C122.453 36.522 121.426 35.187 120.354 33.5"
                  fill="white"
                />
                <path
                  d="M126.98 40.126C125.601 39.3619 124.42 38.4883 123.436 37.5052C122.453 36.522 121.426 35.187 120.354 33.5"
                  stroke="#7F22FE"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
                <path
                  d="M97.4551 62.7344L100.912 66.1914"
                  stroke="#7F22FE"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M98.8198 62.3642L122.602 38.4858L124.97 40.2839L100.905 64.4491L98.8198 62.3642Z"
                  fill="#EDE9FE"
                />
              </svg>
              <h3 className="mb-2 text-xl font-semibold text-gray-900">Notes is empty</h3>
              <p className="text-sm text-gray-600">
                Wait until there's at least one note or create
              </p>
            </div>)
          ) : (
            /* Notes List */
            (<div className="space-y-4 p-4">
              {notes.map((note) => {
                const isMine = userProfile?.uid === note.authorId;
                const isEdited = note.updatedAt && note.updatedAt.toMillis() !== note.createdAt.toMillis();
                const initials = note.authorName.charAt(0).toUpperCase();
                
                return (
                <div
                  key={note.id}
                  className={`flex ${isMine ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`rounded-lg ${isMine ? "border border-blue-200 bg-blue-50" : "bg-destructive-foreground border-border border"} relative w-fit max-w-[85%] p-3 min-[744px]:w-auto min-[744px]:max-w-2xl min-[744px]:p-4`}
                  >
                    <div className="mb-2 flex items-center gap-3">
                      <div className="border-border bg-muted relative inline-flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-md border">
                        <span className="text-sm font-semibold text-gray-900">{initials}</span>
                      </div>
                      <span className="text-sm font-medium text-gray-900">{note.authorName}</span>
                      {isEdited && (
                        <span className="inline-flex items-center rounded border border-gray-600 bg-transparent px-3 py-1 text-xs font-medium text-gray-600">
                          Edited
                        </span>
                      )}
                      {isMine && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="ml-auto flex-shrink-0 rounded p-1 hover:bg-gray-100">
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M8 8.66667C8.36819 8.66667 8.66667 8.36819 8.66667 8C8.66667 7.63181 8.36819 7.33333 8 7.33333C7.63181 7.33333 7.33333 7.63181 7.33333 8C7.33333 8.36819 7.63181 8.66667 8 8.66667Z"
                                  stroke="#475467"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M8 4.00001C8.36819 4.00001 8.66667 3.70153 8.66667 3.33334C8.66667 2.96515 8.36819 2.66667 8 2.66667C7.63181 2.66667 7.33333 2.96515 7.33333 3.33334C7.33333 3.70153 7.63181 4.00001 8 4.00001Z"
                                  stroke="#475467"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M8 13.3333C8.36819 13.3333 8.66667 13.0349 8.66667 12.6667C8.66667 12.2985 8.36819 12 8 12C7.63181 12 7.33333 12.2985 7.33333 12.6667C7.33333 13.0349 7.63181 13.3333 8 13.3333Z"
                                  stroke="#475467"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-40">
                            <DropdownMenuItem
                              onClick={() => handleEditNote(note.id)}
                              className="flex cursor-pointer items-center gap-2"
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M14 5.99999L10 1.99999M1.33337 14.6667L3.46004 14.4467C3.76004 14.4133 3.91004 14.3933 4.04671 14.3467C4.16841 14.3055 4.28404 14.2494 4.39137 14.18C4.51271 14.1013 4.61737 14 4.82671 13.7907L14.3334 4.28399C14.8854 3.73199 14.8854 2.83599 14.3334 2.28399C13.7814 1.73199 12.8854 1.73199 12.3334 2.28399L2.82671 11.7907C2.61737 12 2.51204 12.1053 2.43337 12.2267C2.36398 12.334 2.30791 12.4497 2.26671 12.5713C2.22004 12.708 2.20004 12.858 2.16671 13.158L1.33337 14.6667Z"
                                  stroke="#020618"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                              <span className="text-gray-900">Edit</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleDeleteNote(note.id)}
                              className="flex cursor-pointer items-center gap-2"
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M5.33333 6.66666V10.6667M10.6667 6.66666V10.6667M2 4H14M12.6667 4L12.0887 12.142C12.0344 12.8025 11.4891 13.3333 10.8272 13.3333H5.17282C4.51092 13.3333 3.96563 12.8025 3.91134 12.142L3.33333 4M10 4V2.66666C10 2.29847 9.70152 1.99999 9.33333 1.99999H6.66667C6.29848 1.99999 6 2.29847 6 2.66666V4"
                                  stroke="#FB2C36"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                              <span className="text-gray-900">Delete</span>
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                    <div className="mb-2 text-sm whitespace-pre-wrap text-gray-900">
                      <MentionRenderer content={note.content} />
                    </div>
                    <div className="flex justify-end">
                      <span className="text-xs text-gray-500">{formatTimestamp(note.createdAt)}</span>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>)
          )}

          {/* Input Area */}
          <div className="border-t w-full border-gray-200 px-4 py-3">
            <div className="flex w-full items-center gap-2 min-[744px]:gap-3">
              <div className="flex-1 min-w-0">
                <MentionInput
                  value={editingNoteId ? editingNoteContent : newNote}
                  onChange={(value) =>
                    editingNoteId ? setEditingNoteContent(value) : setNewNote(value)
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      editingNoteId ? handleSaveEdit() : handleSendNote();
                    }
                  }}
                  placeholder={editingNoteId ? "Edit note... (type @ to mention)" : "Add a note... (type @ to mention)"}
                  className="h-10 max-h-[3.5rem] w-full resize-none rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:ring-1 focus:ring-purple-500 focus:outline-none"
                  rows={1}
                  style={{ minHeight: "2.5rem" }}
                  dropdownPosition="top"
                />
              </div>
              {editingNoteId ? (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-10"
                    onClick={() => {
                      setEditingNoteId(null);
                      setEditingNoteContent("");
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveEdit}
                    className="h-10 bg-purple-600 hover:bg-purple-700"
                    size="sm"
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="mr-2"
                    >
                      <path
                        d="M14.1667 17.5V11.6667C14.1667 11.4457 14.0789 11.2337 13.9226 11.0774C13.7663 10.9211 13.5543 10.8333 13.3333 10.8333H6.66667C6.44565 10.8333 6.23369 10.9211 6.07741 11.0774C5.92113 11.2337 5.83333 11.4457 5.83333 11.6667V17.5M5.83333 2.5V5.83333C5.83333 6.05435 5.92113 6.26631 6.07741 6.42259C6.23369 6.57887 6.44565 6.66667 6.66667 6.66667H12.5M12.6667 2.5C13.1063 2.50626 13.5256 2.68598 13.8333 3L17 6.16667C17.314 6.47438 17.4937 6.89372 17.5 7.33333V15.8333C17.5 16.2754 17.3244 16.6993 17.0118 17.0118C16.6993 17.3244 16.2754 17.5 15.8333 17.5H4.16667C3.72464 17.5 3.30072 17.3244 2.98816 17.0118C2.67559 16.6993 2.5 16.2754 2.5 15.8333V4.16667C2.5 3.72464 2.67559 3.30072 2.98816 2.98816C3.30072 2.67559 3.72464 2.5 4.16667 2.5H12.6667Z"
                        stroke="#F8FAFC"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Save
                  </Button>
                </>
              ) : (
                <Button
                  onClick={handleSendNote}
                  disabled={!newNote.trim() || adding}
                  className="h-10 w-10 bg-purple-600 px-0 hover:bg-purple-700 min-[744px]:w-auto min-[744px]:px-4"
                  size="sm"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="min-[744px]:mr-2"
                  >
                    <path
                      d="M18.3336 9.99999C18.3336 10.0789 18.3112 10.1562 18.269 10.2229C18.2268 10.2896 18.1665 10.3429 18.0951 10.3767L3.0951 17.46C3.02018 17.4963 2.93607 17.5093 2.85369 17.4971C2.77132 17.485 2.69449 17.4484 2.6332 17.3921C2.57191 17.3357 2.52898 17.2622 2.51 17.1812C2.49102 17.1001 2.49686 17.0152 2.52677 16.9375L4.8951 10.5817C5.03483 10.2065 5.03483 9.79351 4.8951 9.41832L2.52594 3.06249C2.49588 2.98469 2.48996 2.89964 2.50895 2.81843C2.52794 2.73722 2.57096 2.66361 2.6324 2.60721C2.69384 2.55081 2.77085 2.51423 2.85338 2.50224C2.93592 2.49025 3.02015 2.5034 3.0951 2.53999L18.0951 9.62332C18.1665 9.65707 18.2268 9.71039 18.269 9.77707C18.3112 9.84375 18.3336 9.92106 18.3336 9.99999ZM18.3336 9.99999L5.00008 9.99996"
                      stroke="#F8FAFC"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span className="hidden min-[744px]:inline">{adding ? "Sending..." : "Send"}</span>
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
